import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Empty, Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import { purchasesApi } from "../services/api";
import type { PurchaseItemType, PurchaseSpendingRow } from "../types";
import { PURCHASE_TYPE_LABELS } from "../pages/purchaseTypes";

type Totals = Record<string, number>; // currency -> amount

interface YearRow {
  year: number;
  count: number;
  byType: Partial<Record<PurchaseItemType, Totals>>;
  total: Totals;
}

const TYPE_ORDER: PurchaseItemType[] = ["game", "watch", "perfume"];

function addTo(totals: Totals, currency: string, amount: number) {
  totals[currency] = (totals[currency] || 0) + amount;
}

function formatTotals(totals: Totals | undefined) {
  if (!totals || Object.keys(totals).length === 0) return "-";
  return Object.entries(totals)
    .map(
      ([currency, amount]) =>
        `${amount.toLocaleString("pl-PL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${currency}`,
    )
    .join(" + ");
}

export function PurchaseSpendingCard({
  itemType,
}: {
  itemType?: PurchaseItemType;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["purchases", "summary", itemType],
    queryFn: () => purchasesApi.summary({ item_type: itemType }),
  });

  const { rows, types } = useMemo(() => {
    const byYear = new Map<number, YearRow>();
    const present = new Set<PurchaseItemType>();
    (data?.items || []).forEach((r: PurchaseSpendingRow) => {
      present.add(r.item_type);
      let row = byYear.get(r.year);
      if (!row) {
        row = { year: r.year, count: 0, byType: {}, total: {} };
        byYear.set(r.year, row);
      }
      row.count += r.count;
      const typeTotals = (row.byType[r.item_type] ||= {});
      addTo(typeTotals, r.currency_code, r.total);
      addTo(row.total, r.currency_code, r.total);
    });
    return {
      rows: [...byYear.values()].sort((a, b) => b.year - a.year),
      types: TYPE_ORDER.filter((t) => present.has(t)),
    };
  }, [data]);

  const columns: ColumnsType<YearRow> = [
    { title: "Rok", dataIndex: "year", key: "year", width: 90 },
    { title: "Liczba", dataIndex: "count", key: "count", width: 90 },
    // Per-type breakdown only makes sense when not filtered to one type
    ...(itemType || types.length < 2
      ? []
      : types.map((t) => ({
          title: PURCHASE_TYPE_LABELS[t].plural,
          key: t,
          render: (_: unknown, r: YearRow) => formatTotals(r.byType[t]),
        }))),
    {
      title: "Suma",
      key: "total",
      render: (_: unknown, r: YearRow) => (
        <strong>{formatTotals(r.total)}</strong>
      ),
    },
  ];

  const title = itemType
    ? `Wydatki rocznie — ${PURCHASE_TYPE_LABELS[itemType].plural.toLowerCase()}`
    : "Wydatki rocznie";

  return (
    <Card title={title} size="small" style={{ marginBottom: 16 }}>
      {!isLoading && rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Brak zakupów"
        />
      ) : (
        <Table
          rowKey="year"
          size="small"
          columns={columns}
          dataSource={rows}
          loading={isLoading}
          pagination={false}
        />
      )}
    </Card>
  );
}

export default PurchaseSpendingCard;
